import {
  useState,
  useEffect,
  useMemo,
  useContext,
  MouseEventHandler,
} from 'react';
import useSWRInfinite from 'swr/infinite';
import { ParsedUrlQuery } from 'querystring';
import { Dropdown, Button, useModal, Link } from '@appsumo/dorado-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretUp } from '@fortawesome/free-solid-svg-icons';
import { Transition } from '@headlessui/react';
import { InView } from 'react-intersection-observer';

import { useUserStatus } from '~/hooks/userStatus';
import { UserObjectProps } from '~/types/user';
import type { Deal } from '~/types/deal';
import useUser from '~/lib/user';
import { DEAL_COMMENT_TYPE, DISCUSSION } from '~/lib/discussions/constants';
import { getDiscussionsUrl, getComments } from '~/lib/discussions/fetch';
import {
  Comment as CommentType,
  DiscussionSort,
  DiscussionFilter,
  DiscussionFilterOption,
  DiscussionSortOption,
} from '~/lib/discussions/types';
import { PDPContext } from '~/lib/product/context';

import { Children, Comment, ReplyModal } from '~/components/discussions';
import { Loader } from '~/components/ui';
import { useRouter } from 'next/router';
import { getHashFromPath, getQueryFromPath } from '~/lib/util';
import { useExperiment } from '~/lib/experiment';
import { AGGREGATE_PAGE_EXPERIMENT_NAME } from '~/lib/util/constants';

/**
 * Replies to a comment (thread) allowing children to hide/show.
 */
function CommentThread({
  comment,
  mutateComment,
  moderator = false,
  showAllReplies = true,
  isReview = false,
}: {
  comment: CommentType;
  mutateComment: () => void;
  moderator?: boolean;
  showAllReplies: boolean;
  isReview?: boolean;
}) {
  const [showReplies, setShowReplies] = useState(showAllReplies);

  // when the show all/hide all replies changes update this node
  useEffect(() => {
    setShowReplies(showAllReplies);
  }, [showAllReplies]);

  const replyCount = useMemo(() => {
    return (
      (comment.children ? comment.children.length : 0) +
      (comment.children
        ? comment.children.reduce(
            (sum, child) => sum + (child.children ? child.children.length : 0),
            0,
          )
        : 0)
    );
  }, [comment]);

  return (
    <div key={comment.id}>
      <div className="rounded-lg shadow-lg">
        <Comment
          href={comment.display_path}
          comment={comment}
          moderator={moderator}
          mutateComment={mutateComment}
          isReview={isReview}
        />
        {comment.children && comment.children.length > 0 && (
          <div className="border-t bg-gray-100 p-4 sm:px-12">
            <div className="flex items-end justify-end">
              <button
                className="group inline-flex items-center gap-x-2 text-xs text-gray-500"
                onClick={() => setShowReplies(!showReplies)}
              >
                <FontAwesomeIcon
                  icon={faCaretUp}
                  className={`${showReplies ? '' : 'rotate-180'}`}
                />
                {showReplies ? 'Hide' : 'Show'} replies
                <div className="h-5 w-5 rounded-full border border-gray-500 text-center align-middle text-xs transition delay-150 ease-in-out group-hover:border-yellow-500 group-hover:text-yellow-500">
                  {replyCount}
                </div>
              </button>
            </div>
            <Transition
              show={showReplies}
              enter="transition-[max-height] duration-300 ease-in-out overflow-hidden"
              enterFrom="max-h-[0px]"
              enterTo="max-h-none"
              leave="transition-[max-height] duration-300 ease-in-out overflow-hidden"
              leaveFrom="max-h-none"
              leaveTo="max-h-[0px]"
            >
              <Children
                root={comment}
                comments={comment.children}
                moderator={moderator}
                mutateComment={mutateComment}
                isReview={isReview}
              />
            </Transition>
          </div>
        )}
      </div>
    </div>
  );
}

function LoginOrVerifyToComment({
  user,
  commentType,
}: {
  user?: UserObjectProps;
  commentType: DEAL_COMMENT_TYPE;
}) {
  if (user?.email_verified || user?.is_staff) return <></>;

  return (
    <div className="rounded bg-iceberg px-5 py-3 text-center md:w-1/2">
      <p>
        Please&nbsp;
        <Link href={user?.id ? '/account/email/' : '/accounts/login/'} styled>
          {user?.id ? 'verify your email' : 'log in'}
        </Link>
        &nbsp;to&nbsp;
        {commentType === DEAL_COMMENT_TYPE.QUESTION
          ? 'ask a question'
          : 'leave a review'}
        .
      </p>
    </div>
  );
}

function EmptyCommentsCard({
  type,
  deal,
  onClick,
  disabled,
}: {
  type: string;
  deal: Deal;
  onClick: () => void;
  disabled: boolean;
}) {
  const header =
    type === DEAL_COMMENT_TYPE.REVIEW
      ? 'No one has reviewed this product... yet'
      : 'No one has asked a question... yet';
  const description =
    type === DEAL_COMMENT_TYPE.REVIEW
      ? 'Be the first to write a review. (Purchase required.)'
      : `Be the first to ask a question.  Get a reply from the ${deal.public_name} team.`;

  return (
    <div className="flex flex-col gap-4 rounded border border-gravel p-6 shadow-md">
      <h4 className="font-header text-3xl font-bold">{header}</h4>
      <p>{description}</p>
      <div>
        <Button disabled={disabled} onClick={onClick} secondary>
          Write a {type === DEAL_COMMENT_TYPE.REVIEW ? 'review' : 'question'}
        </Button>
      </div>
    </div>
  );
}

const filterOptions: DiscussionFilterOption[] = [
  { value: 'all', label: 'All' },
  { value: 'unresolved', label: 'Unresolved' },
  { value: 'unapproved', label: 'Pending' },
];

interface CommentParams extends ParsedUrlQuery {
  filter?: DiscussionFilter;
  sort?: DiscussionSort;
  query?: string;
}

function MoreCommentsCTA({
  commentType,
  onClick,
  queryParams,
}: {
  commentType: DEAL_COMMENT_TYPE;
  queryParams?: URLSearchParams;
  onClick: MouseEventHandler<HTMLButtonElement>;
}) {
  const { variant: aggregateVariant } = useExperiment(
    AGGREGATE_PAGE_EXPERIMENT_NAME,
  );
  const {
    deal: { slug: dealSlug },
  }: { deal: Deal } = useContext(PDPContext);

  const textType =
    commentType === DEAL_COMMENT_TYPE.REVIEW ? 'reviews' : 'questions';

  if (aggregateVariant === 'enabled') {
    const params = new URLSearchParams(queryParams);
    params?.append('ref', 'pdp');

    return (
      <Link
        className="mt-2 rounded-full bg-yellow-400 px-4 py-2 text-xs font-medium"
        href={`/products/${dealSlug}/${textType}?${params.toString()}`}
        target="_blank"
      >
        See all {textType}
      </Link>
    );
  }

  return (
    <button
      type="button"
      className="mt-2 rounded-full bg-yellow-400 px-4 py-2 text-xs font-medium"
      onClick={onClick}
    >
      Show more {textType}
    </button>
  );
}

/**
 * Render a stream of comments or deal reviews on a PDP tab.
 */
export function DealComments({
  dealId,
  fallbackData,
  type,
  sortOptions,
  total,
  updateTotal,
  query,
  enableParamsState = false,
}: {
  dealId: number;
  fallbackData: any[];
  type: DEAL_COMMENT_TYPE;
  sortOptions: DiscussionSortOption[];
  total: number;
  updateTotal: (total: number) => void;
  query?: string;
  enableParamsState?: boolean;
}) {
  const { deal }: { deal: Deal } = useContext(PDPContext);
  const { user } = useUser();
  const { userStatus } = useUserStatus(dealId);
  // current sort value
  const [sortBy, setSortBy] = useState<DiscussionSort>(sortOptions[0].value);
  // current filter value
  const [filterBy, setFilterBy] = useState<string>(filterOptions[0].value);
  // holds the state for show all/hide all replies
  const [showReplies, setShowReplies] = useState(true);

  const {
    asPath,
    query: params,
  }: {
    asPath: string;
    query: CommentParams;
  } = useRouter();

  const aggregateUrl = `/products/${deal.slug}/${
    type === 'dealcomment' ? 'questions' : 'reviews'
  }`;
  const isAggregatePage = asPath.startsWith(aggregateUrl);
  const { variant: aggregateVariant } = useExperiment(
    AGGREGATE_PAGE_EXPERIMENT_NAME,
  );

  // load comments using infinite loader
  const { data, size, setSize, error, mutate } = useSWRInfinite(
    (pageIndex, previousPageData) => {
      // construct URL for next page of data
      const url = getDiscussionsUrl({
        dealId,
        type,
        page: pageIndex + 1,
        sort: sortBy,
        filter: filterBy as DiscussionFilter,
        query,
        paginateOnSearch: aggregateVariant === 'enabled' && !isAggregatePage,
      });

      // TODO: recognise the end of stream and return no more
      if (previousPageData === null) {
        //return null;
      }

      return url;
    },
    {
      fallbackData: query ? [] : fallbackData, // only use fallback data when not searching
      revalidateFirstPage: false,
      fetcher: getComments,
    },
  );

  const [showModal, hideModal] = useModal(
    () => (
      <ReplyModal
        hideModal={hideModal}
        deal={deal}
        type={
          type === DEAL_COMMENT_TYPE.QUESTION ? 'dealcomment' : 'dealreview'
        }
        mutateComment={mutate}
      />
    ),
    [],
  );

  // reset paginator when sort changes
  useEffect(() => {
    mutate([], true);
    setSize(1);
  }, [sortBy, mutate, setSize]);

  const isSearching = !!query;
  // construct data for rendering by combing all pages into a single stream of comments
  const comments = useMemo(() => (data ? [].concat(...data) : []), [data]);
  const isLoadingInitialData = typeof data === 'undefined' && !error;
  const isLoadingMore =
    isLoadingInitialData ||
    (size > 0 && data && typeof data[size - 1] === 'undefined');
  const isFinishedPaginating =
    size > 0 && data && data[data.length - 1]?.length === 0;

  // update total comments
  useEffect(() => {
    if (isSearching) {
      updateTotal(comments.length);
    }
  }, [comments, isSearching, updateTotal]);

  // current sort label
  const currentSort = useMemo(() => {
    let [intialSort] = sortOptions;
    const findSort = (f: DiscussionSortOption) => f.value === params?.sort;

    if (params?.sort && sortOptions.some(findSort)) {
      intialSort = sortOptions.find(findSort) as DiscussionSortOption;
      setSortBy(intialSort.value);
    }

    return intialSort.label;
  }, [params?.sort, sortOptions]);

  // current filter label
  const currentFilter = useMemo(() => {
    let [initialFilter] = filterOptions;
    const findFilter = (f: DiscussionFilterOption) =>
      f.value === params?.filter;

    if (
      userStatus?.user_moderator &&
      params?.filter &&
      filterOptions.some(findFilter)
    ) {
      initialFilter = filterOptions.find(findFilter) as DiscussionFilterOption;
      setFilterBy(initialFilter.value);
    }

    return initialFilter.label;
  }, [params.filter, userStatus?.user_moderator]);

  const isEmptyComments = comments.length === 0;

  const statusCounts = useMemo(() => {
    let unresolved = 0;
    let pending = 0;

    const traverseComments = (comments: CommentType[]) => {
      comments.forEach((comment: CommentType) => {
        if (!comment.resolved) {
          unresolved++;
        }

        if (comment.approved === null) {
          pending++;
        }

        if (comment.children?.length) {
          traverseComments(comment?.children);
        }
      });
    };

    traverseComments(comments);

    return {
      unresolved,
      pending,
    };
  }, [comments]);

  const allowCreateItem = useMemo(() => {
    // unauthenticated users can't do anything
    if (!user) {
      return false;
    }

    // need to purchase to write a review
    if (type === DEAL_COMMENT_TYPE.REVIEW) {
      return !!userStatus?.user_has_purchased;
    }

    return true;
  }, [type, userStatus, user]);

  useEffect(() => {
    const hash = getHashFromPath(asPath);
    const params = getQueryFromPath(asPath);
    const rating = Number(params.get('rating'));

    if (
      type === DEAL_COMMENT_TYPE.REVIEW &&
      hash === DISCUSSION.REVIEWS.toString() &&
      allowCreateItem &&
      rating >= 1 &&
      rating <= 5
    ) {
      showModal();
    }
  }, [allowCreateItem, asPath, showModal, type]);

  useEffect(() => {
    if (enableParamsState) {
      const url = new URL(window.location.href);

      if (filterBy !== filterOptions[0].value) {
        url.searchParams.set('filter', filterBy);
      } else {
        url.searchParams.delete('filter');
      }

      if (sortBy !== sortOptions[0].value) {
        url.searchParams.set('sort', sortBy);
      } else {
        url.searchParams.delete('sort');
      }

      window.history.replaceState({}, '', url.toString());
    }
  }, [sortBy, filterBy, enableParamsState, sortOptions]);

  const isEmailVerified = !!user?.email_verified;

  // set up params for aggregate page
  const aggregateParams = useMemo(() => {
    const params = new URLSearchParams();

    if (aggregateVariant === 'enabled') {
      if (query) {
        params.set('query', query);
      }

      // not sending first option as it's default
      if (sortBy !== sortOptions[0].value) {
        params.set('sort', sortBy);
      }

      // not sending first option as it's default
      if (filterBy !== filterOptions[0].value) {
        params.set('filter', filterBy);
      }
    }

    return params;
  }, [aggregateVariant, filterBy, query, sortBy, sortOptions]);

  const onScrollNextPage = (inView: boolean) => {
    if (inView) {
      setSize(size + 1);
    }
  };

  return (
    <>
      {total ? (
        <div className="flex w-full items-center max-sm:flex-col max-sm:items-start max-sm:gap-y-4 sm:gap-x-4">
          <div className="flex max-sm:w-full max-sm:flex-col max-sm:gap-y-4">
            {userStatus?.user_moderator && (
              <Dropdown
                labelPrefix="Filter by:"
                defaultValue={{ label: currentFilter, value: filterBy }}
                options={filterOptions}
                onClick={(value) =>
                  setFilterBy(value.value as DiscussionFilter)
                }
                className="mr-4 text-xs max-sm:w-full"
              />
            )}
            <Dropdown
              labelPrefix="Sort by:"
              defaultValue={{ label: currentSort, value: sortBy }}
              options={sortOptions}
              onClick={(value) => setSortBy(value.value as DiscussionSort)}
              className="text-xs max-sm:w-full"
            />
          </div>
          <div className="flex grow justify-between max-sm:w-full">
            <button
              onClick={() => setShowReplies(!showReplies)}
              className="text-sm text-blue-600"
            >
              {showReplies ? 'Hide' : 'Show'} all replies
            </button>
            <div>
              Showing <strong>{comments.length}</strong> of{' '}
              <strong>{isSearching ? comments.length : total}</strong>{' '}
              {type === 'dealcomment' ? 'questions' : 'reviews'}
            </div>
          </div>
        </div>
      ) : null}
      {!user?.email_verified && !user?.is_staff && (
        <div className="px-4">
          <LoginOrVerifyToComment user={user} commentType={type} />
        </div>
      )}
      {userStatus?.user_moderator && (
        <div>
          <span className="text-sm">
            Unresolved: <strong>{statusCounts.unresolved}</strong>
          </span>
          <span className="ml-3 text-sm">
            Pending Moderation: <strong>{statusCounts.pending}</strong>
          </span>
        </div>
      )}
      {(user?.email_verified || user?.is_staff) && (
        <div>
          <Button
            onClick={() => showModal()}
            disabled={
              !allowCreateItem ||
              !isEmailVerified ||
              (userStatus?.user_has_reviewed && type === 'dealreview')
            }
            secondary
          >
            Submit a {type === 'dealcomment' ? 'question' : 'review'}
          </Button>
        </div>
      )}
      {isEmptyComments && (
        <EmptyCommentsCard
          type={type}
          deal={deal}
          onClick={() => showModal()}
          disabled={!allowCreateItem || !isEmailVerified}
        />
      )}
      <div className="flex flex-col gap-y-5 sm:gap-y-10">
        {comments.map((comment: any) => (
          <CommentThread
            key={comment.id}
            comment={comment}
            showAllReplies={showReplies}
            moderator={userStatus?.user_moderator}
            mutateComment={mutate}
            isReview={type === DEAL_COMMENT_TYPE.REVIEW}
          />
        ))}
      </div>
      {!isAggregatePage &&
        !isLoadingMore &&
        !isFinishedPaginating &&
        !isSearching && (
          <div className="my-5 flex items-center justify-center">
            <MoreCommentsCTA
              commentType={type}
              queryParams={aggregateParams}
              onClick={() => setSize(size + 1)}
            />
          </div>
        )}

      {isAggregatePage &&
        !isLoadingMore &&
        !isFinishedPaginating &&
        !isSearching && (
          <InView as="div" onChange={onScrollNextPage} triggerOnce>
            <div className="w-full">&nbsp;</div>
          </InView>
        )}

      {isLoadingMore && !isEmptyComments && (
        <div className="my-5 flex items-center justify-center">
          <Loader />
        </div>
      )}
    </>
  );
}
